/**
 * The Test it trace, adapted ONCE — the only place in the fork that knows the field names
 * the BFF chose. Step-driven tasks, project 1, delivery 1a.
 *
 * WHY A MODULE AND NOT A `computed` INSIDE THE COMPONENT. The step-driven engine's trace
 * crosses two repos and was designed in two of them at the same time: the sidecar names
 * the run `trace.task` (`http/playground.ts`), which on the BFF's wire is already spent
 * on the FREE-TEXT playbook pointer, so the BFF renames it — and the names inside it are
 * translated on the way through as well (`ok` → `worked`, `blocked` → `withheld`). A
 * template that read `run.connections[i].ok` in one branch and `run.next` in another
 * would spread that agreement across a dozen `v-if`s, where a rename becomes a dozen
 * silent empties. Here it is one function, with one spec, and a rename is one edit.
 *
 * WHAT IT PROMISES ITS CALLER — and every one of these is load-bearing for a screen whose
 * whole job is being readable when something has gone wrong:
 *
 *  • IT NEVER THROWS. Not on `undefined`, not on a string where an object belongs, not on
 *    a `connections` that arrived as an object. The trace is the disclosure an owner opens
 *    BECAUSE the answer was wrong; a render error there costs her the answer as well.
 *  • ABSENT AND EMPTY COLLAPSE TO `null`, NEVER TO A SCAFFOLD. Three real cases produce
 *    no step trace at all — a turn on an account without the engine, a turn that handed
 *    off before the engine ran, and a sidecar older than the feature — and all three must
 *    render as "this turn did not run a task", which is a heading that is absent, not a
 *    heading over four empty rows.
 *  • IT NARROWS NOTHING IT CANNOT NAME. `next` and a gate reason's `kind` are plain
 *    strings on the wire on purpose: a value added upstream has to reach the screen rather
 *    than be rejected by a client that shipped first. An outcome this screen has no
 *    sentence for becomes `null` and that line is simply not drawn.
 *
 * WHERE THE WHOLE CROSS-REPO RULE IS WRITTEN DOWN, in the form neither repo can state on its
 * own: `../waslo-services/docs/step-driven-tasks.md` §4.4 ("The Test it trace's wire — what
 * the fork depends on"), under "Every key `testReplyDTO` publishes is PRESENT". Read it
 * before adding a field to this trace. The short version is
 * the deploy asymmetry: the DATA half of this feature ships with the app tier in ninety
 * seconds, this half needs a branded-image rebuild, so the two are never live at the same
 * instant and every field has to be additive in both directions.
 *
 * WHERE THE LOUD FAILURE LIVES INSTEAD: `api/client.js` (`readTestReply`), which reads the
 * same two fields strictly the moment they are present — a `worked` that arrived as a
 * string is a bug between two repos and is answered with `MALFORMED`, not with a missing
 * chip. This module is the second half of that pair, not a softer copy of it: by the time
 * a value reaches here it has already been checked, and everything below is what keeps the
 * component honest when it has NOT (an older reply replayed from a fixture, a future field,
 * a hand-built object in a test).
 */

/**
 * WHERE THE STEP TRACE HANGS, as a literal, because this is the one thing the two repos
 * can disagree about in complete silence. Confirmed against the BFF's own DTO
 * (`services/authoring-web/src/routes/api/playground.ts`) and pinned from this side in
 * `specs/bff-contract.spec.js`.
 */
export const STEP_TRACE_KEY = 'stepTask';

/** The reply gate's verdict — why a draft the owner never saw was held back. */
export const GATE_TRACE_KEY = 'gate';

/**
 * `stepTask.next` — what the turn ended up doing. This screen has one sentence per value;
 * anything else is a value from a newer sidecar and draws no line at all.
 */
export const STEP_OUTCOMES = [
  'ask',
  'say',
  'silent',
  'done',
  'handoff',
  'none',
];

/**
 * `next: 'none'` — "the engine looked at this turn and is not driving it": the ordinary
 * free-text assistant answered, as it does on every off-task message of a switched-on
 * account. It is therefore NOT evidence that a task ran, and it is the reason `stepOf`
 * weighs the other six fields and not this one.
 *
 * Get this wrong and the cost is not subtle: a switched-on account would carry a "The task
 * it is working through" panel, empty, under every ordinary reply it ever gets.
 */
const STEP_OUTCOME_IDLE = 'none';

/**
 * `gate.reasons[].kind`. `url` and `price` are GROUNDING — the assistant wrote something
 * nothing it was given backs up, and those are the two that get a draft withheld.
 * `length` and `language` are soft: the draft was rewritten and sent.
 * `claim`, `time` and `list` (P-10.5, P-10.7) are removable: the sentence that said it was
 * taken out and the rest went. `prompt_echo` (P-2.4) is its own instructions coming back.
 */
export const GATE_REASON_KINDS = [
  'url',
  'price',
  'length',
  'language',
  'claim',
  'prompt_echo',
  'time',
  'list',
];

/** P-5.7: what one tool call was. Only `connection` leaves the chat. */
export const ACTION_KINDS = [
  'lookup',
  'handover',
  'menu',
  'chat_action',
  'connection',
];
export const CHAT_ACTION_KEYS = [
  'close',
  'assign',
  'add_labels',
  'remove_labels',
  'note',
  'save_contact',
];

/**
 * The kind of a call a BFF one version behind did not name, by the sidecar's own tool names
 * (`tools/index.ts` ESCALATE_TOOL_NAME, `menus/control-tool.ts` ATTACH_MENU_TOOL_NAME, the
 * native action catalogue). Anything else is one of HER connections.
 */
const KIND_BY_NAME = {
  search_knowledge_base: 'lookup',
  escalate_to_human: 'handover',
  attach_menu: 'menu',
  close_conversation: 'chat_action',
  assign_conversation: 'chat_action',
  add_labels: 'chat_action',
  remove_labels: 'chat_action',
  add_private_note: 'chat_action',
  save_customer_details: 'chat_action',
};
const ACTION_BY_NAME = {
  close_conversation: 'close',
  assign_conversation: 'assign',
  add_labels: 'add_labels',
  remove_labels: 'remove_labels',
  add_private_note: 'note',
  save_customer_details: 'save_contact',
};

const isRecord = value =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const asList = value => (Array.isArray(value) ? value : []);

/** A string, or `null` — and a blank one is `null`, since it renders as an empty row. */
const line = value =>
  typeof value === 'string' && value.trim() !== '' ? value : null;

/** A flag, or `null` for "nobody said" — which is NOT the same claim as `false`. */
const flag = value => (typeof value === 'boolean' ? value : null);

/**
 * One saved answer, as the engine holds it.
 *
 * `value` and `label` are two different facts and the screen needs both: `label` is what
 * the customer saw and tapped ("Tomorrow, 4pm"), `value` is what a connection is handed
 * for it (`2026-09-26T16:00`). They are equal for anything typed, so the second is drawn
 * ONLY when it differs — printing `Tomorrow, 4pm / Tomorrow, 4pm` on forty rows buries the
 * two rows where the mapping is the bug.
 */
const answerOf = raw => {
  if (!isRecord(raw)) return null;
  const key = line(raw.key);
  const label = line(raw.label);
  const value = line(raw.value);
  const shown = label ?? value;
  if (key === null && shown === null) return null;
  return {
    key,
    label: shown,
    /** The stored form, only when it is something other than what was shown. */
    value: value !== null && value !== shown ? value : null,
  };
};

/**
 * P-2.11: where a connection's answer came from on a rehearsal. `live`: the real system was
 * asked. `sample`: the saved sample stood in for it. `simulated`: a made-up answer. `no_sample`:
 * a live list with no sample, so the step asked with no rows.
 */
export const CONNECTION_SOURCES = ['live', 'sample', 'simulated', 'no_sample'];

/** P-10.9: how the task that started this turn was started. */
export const STARTED_BY = ['assistant', 'words', 'menu', 'switch', 'rehearsal'];

/** A step named on a trace line: the owner's words for it, else its id. `null` for neither. */
const namedOf = raw => {
  if (!isRecord(raw)) return null;
  return line(raw.stepLabel) ?? line(raw.stepId);
};

/**
 * One connection the engine ran for this task.
 *
 * `rehearsed` is carried per row for exactly the reason the tool-call rows carry it: with
 * the live switch off a connection is SIMULATED, and a row that says "worked" about a
 * system nobody called is the one lie this screen must not tell. `null` (a trace from
 * before the flag) draws no badge at all rather than guessing which it was.
 */
const connectionOf = raw => {
  if (!isRecord(raw)) return null;
  const name = line(raw.name);
  if (name === null) return null;
  return {
    name,
    worked: flag(raw.worked),
    rehearsed: flag(raw.rehearsed),
    /** P-2.11: where the answer came from; `null` for a value this screen has no line for. */
    source: CONNECTION_SOURCES.includes(raw.source) ? raw.source : null,
    /** When the sample was taken (`source: 'sample'`), else `null`. */
    sampleAt: line(raw.sampleAt),
    /** The connection's id, for "Fetch a sample now"; `null` when not known. */
    connectionId: Number.isInteger(raw.connectionId) ? raw.connectionId : null,
  };
};

/**
 * A step, named twice — the owner's own words for it, and the id underneath.
 *
 * THE ID IS THE FACT AND THE LABEL IS DECORATION OVER IT, which is why the fallback runs in
 * that direction and never the other, and why the id is not thrown away when a label exists.
 * `day` means nothing to a shop owner reading why her booking went wrong; "Which day" means
 * everything. But when the graph itself is what misbehaved, the id is the only string that
 * finds that step again in the builder, so it is kept and drawn beside the label.
 *
 * The label is genuinely absent in three real cases and must not be waited for: a step whose
 * label the owner left blank, a definition she edited under a run already in flight, and any
 * moment in a rollout where the sidecar is older than the BFF in front of it.
 *
 * @param {unknown} label The owner-authored label. Rendered as user content, never as markup.
 * @param {unknown} id The stable step id.
 * @param {'step'|'rule'} as Which pair of fields to produce.
 */
const named = (label, id, as) => {
  const shown = line(label);
  const code = line(id);
  return {
    /** What the sentence says. */
    [`${as}Name`]: shown ?? code,
    /** The id, drawn as a muted chip — but only when it is not already the line above. */
    [`${as}Code`]: shown === null ? null : code,
    /** True when the owner's own wording was available, which changes the sentence. */
    [`${as}Named`]: shown !== null,
  };
};

/** `[{ label, value }]`, strings only. Anything else is dropped, never printed as `[object]`. */
const linesOf = raw =>
  asList(raw)
    .filter(isRecord)
    .map(row => ({ label: line(row.label), value: line(row.value) ?? '' }))
    .filter(row => row.label !== null);

/**
 * One tool call, in the shape the trace draws (P-5.7). `kind` is the BFF's when it sent one,
 * else worked out from the name; `effect` is `inside_chat` for everything but a connection,
 * which is `rehearsed` or `live`. `Object.hasOwn`, never `in`: a tool named `constructor`
 * is her connection, not a hit on `Object.prototype`.
 */
const actionOf = raw => {
  const name = line(raw.name) ?? '';
  const byName = Object.hasOwn(KIND_BY_NAME, name)
    ? KIND_BY_NAME[name]
    : 'connection';
  const kind = ACTION_KINDS.includes(raw.kind) ? raw.kind : byName;
  const rehearsed =
    typeof raw.rehearsed === 'boolean'
      ? raw.rehearsed
      : raw.effect === 'rehearsed';
  let effect = 'inside_chat';
  if (kind === 'connection') {
    effect =
      raw.effect === 'live' || raw.effect === 'rehearsed'
        ? raw.effect
        : (rehearsed && 'rehearsed') || 'live';
  }
  const byNameAction = Object.hasOwn(ACTION_BY_NAME, name)
    ? ACTION_BY_NAME[name]
    : null;
  return {
    name,
    kind,
    effect,
    rehearsed: effect === 'rehearsed',
    label: line(raw.label) ?? (kind === 'connection' ? name : null),
    action:
      kind === 'chat_action'
        ? (CHAT_ACTION_KEYS.includes(raw.action) && raw.action) || byNameAction
        : null,
    fields: linesOf(raw.fields),
    received: linesOf(raw.received),
    input: raw.input,
    output: raw.output,
  };
};

/**
 * `reply.trace.stepTask` → what the screen draws, or `null` when there is nothing to draw.
 *
 * The final emptiness check is the point of the function. A run that reports a task, or a
 * step, or one saved answer, or one connection, or an outcome OTHER THAN `none` is worth a
 * section. A run that reports none of those is a turn the engine did not take part in,
 * whatever key it arrived under, and the right rendering of that is silence.
 *
 * FOUR DIFFERENT CAUSES CONVERGE ON THAT SILENCE, and they must, because none of them is
 * something an owner asked about: the account has the engine switched off (`null` — every
 * account today); the sidecar predates it (the key is absent); the assistant handed over
 * before the engine was reached; and the engine looked at an off-task message and let the
 * ordinary assistant answer it (`next: 'none'`, with nothing else set — the common case on
 * a switched-on account, and the one that would otherwise draw an empty panel under every
 * single reply).
 */
const stepOf = raw => {
  if (!isRecord(raw)) return null;
  const step = {
    /** P-5.7: the title she gave the task; its internal name only when there is no title. */
    task: line(raw.taskTitle) ?? line(raw.taskName),
    ...named(raw.stepLabel, raw.stepId, 'step'),
    ...named(raw.ruleFailedLabel, raw.ruleFailed, 'rule'),
    answers: asList(raw.answers).map(answerOf).filter(Boolean),
    connections: asList(raw.connections).map(connectionOf).filter(Boolean),
    /** P-10.3: answers not saved because the customer did not say them. */
    dropped: asList(raw.dropped).map(namedOf).filter(Boolean),
    /** P-10.22: Calls not sent again, because they already worked in this chat. */
    alreadyDone: asList(raw.alreadyDone).map(namedOf).filter(Boolean),
    outcome: STEP_OUTCOMES.includes(raw.next) ? raw.next : null,
    /** P-10.21: why the engine passed the chat on (`system_refused`), or `null`. */
    handoverCause: line(raw.handoverCause),
    /** P-10.2: the message went out as written (a read-back, an exact Tell), no AI reply. */
    sentAsWritten: raw.sentAsWritten === true,
    /** P-10.9: how the task was started this turn, or `null`. */
    startedBy: STARTED_BY.includes(raw.startedBy) ? raw.startedBy : null,
  };
  const saidSomething =
    step.task !== null ||
    step.startedBy !== null ||
    step.stepName !== null ||
    step.ruleName !== null ||
    (step.outcome !== null && step.outcome !== STEP_OUTCOME_IDLE) ||
    step.answers.length > 0 ||
    step.connections.length > 0 ||
    step.dropped.length > 0 ||
    step.alreadyDone.length > 0;
  return saidSomething ? step : null;
};

/**
 * `reply.trace.gate` → why a draft was held back or rewritten, or `null` when the gate had
 * nothing to say (the ordinary case, on the great majority of turns).
 *
 * THE FEATURE THIS EXISTS FOR: until now a withheld draft reached the Test it screen as a
 * bare handover line, identical to the assistant deciding on its own that a person was
 * needed. Those are opposite problems — one is the assistant working as designed, the
 * other is the assistant having written something it could not back up — and an owner who
 * cannot tell them apart tunes the wrong thing.
 */
const gateOf = raw => {
  if (!isRecord(raw)) return null;
  const withheld = raw.withheld === true;
  const retried = raw.retried === true;
  const reasons = asList(raw.reasons)
    .filter(isRecord)
    .map(reason => ({
      /** `null` for a kind added after this screen shipped: a generic sentence, not a throw. */
      kind: GATE_REASON_KINDS.includes(reason.kind) ? reason.kind : null,
      value: line(reason.value),
    }));
  if (!withheld && !retried && reasons.length === 0) return null;
  return { withheld, retried, reasons };
};

/** `reply.trace.task` — the FREE-TEXT playbook pointer, which predates the step engine. */
const scenarioOf = raw => {
  const scenario = isRecord(raw) ? raw : {};
  return {
    started: line(scenario.started),
    completed: scenario.completed === true,
    active: line(scenario.active),
  };
};

/**
 * The whole trace, in the shape the template reads.
 *
 * @param {unknown} raw `reply.trace` as `api/client.js` returns it.
 * @returns {{
 *   actions: {name, kind, effect, rehearsed, label, action, fields, received, input, output}[],
 *   connectionActions: object[], chatActions: object[],
 *   lookups: {query: string, matches: {question: string, score: number}[]}[],
 *   matchCount: number,
 *   scenario: {started: string|null, completed: boolean, active: string|null},
 *   hasScenario: boolean,
 *   step: object|null,
 *   gate: object|null,
 *   isEmpty: boolean,
 * }}
 */
export const normaliseTestTrace = raw => {
  const trace = isRecord(raw) ? raw : {};
  const actions = asList(trace.actions).filter(isRecord).map(actionOf);
  const lookups = asList(trace.lookups)
    .filter(isRecord)
    .map(lookup => ({ ...lookup, matches: asList(lookup.matches) }));
  const scenario = scenarioOf(trace.task);
  const step = stepOf(trace[STEP_TRACE_KEY]);
  const gate = gateOf(trace[GATE_TRACE_KEY]);
  const hasScenario =
    scenario.started !== null || scenario.completed || scenario.active !== null;

  return {
    actions,
    /** P-5.7: her systems, the only calls that leave the chat and carry a badge. */
    connectionActions: actions.filter(action => action.kind === 'connection'),
    /** P-5.7: what it did inside this chat. No badge. */
    chatActions: actions.filter(action => action.kind !== 'connection'),
    lookups,
    matchCount: lookups.reduce(
      (total, lookup) => total + lookup.matches.length,
      0
    ),
    scenario,
    hasScenario,
    step,
    gate,
    /**
     * Nothing in the trace itself. The MENU is deliberately not counted here — it is a
     * separate prop on the component, it is drawn under the reply whether or not this
     * disclosure is open, and folding it in would make this module's answer depend on
     * something it was never handed.
     */
    isEmpty:
      actions.length === 0 &&
      lookups.length === 0 &&
      !hasScenario &&
      step === null &&
      gate === null,
  };
};

export default normaliseTestTrace;
